import { useState } from 'react';
import type { ActivityInput } from '@elka/types';
import { useActivities } from './useActivities';
import { useTicketActions } from '../../tickets/useTicketActions';
import { buildCallTitle, buildFollowUpTask, stripBlank } from '../utils/callActivity';
import type { FollowUpData } from '../utils/callActivity';

export interface LogActivityOptions {
  /** When provided, a follow-up task (a tasks-app ticket) is created in the same save. */
  followUp?: FollowUpData;
}

/**
 * Single write/orchestration hook shared by the fast call-log flow and the
 * generic ActivityForm, so every entry point behaves identically:
 *  - auto-titles when the title is blank (never writes an empty title)
 *  - strips blank/undefined fields before writing (Firestore rejects undefined)
 *  - creates the activity and, if requested, a follow-up ticket in one call
 *
 * Throws on failure so callers can keep the form open and surface an error.
 */
export function useLogActivity() {
  const { addActivity } = useActivities();
  const { createTicket } = useTicketActions();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const logActivity = async (input: ActivityInput, options?: LogActivityOptions) => {
    setIsSubmitting(true);
    try {
      const followUp = options?.followUp;
      const title = input.title?.trim() || buildCallTitle(input);
      // Stamp the follow-up onto the activity too so it shows the "מעקב" badge,
      // in addition to creating the follow-up ticket below.
      const cleaned = stripBlank({
        ...input,
        title,
        ...(followUp ? { followUpDate: followUp.date, followUpNotes: followUp.notes } : {}),
      }) as ActivityInput;

      // Create the follow-up ticket first so its id can be linked onto the activity in a
      // single write.
      let followUpTaskId: string | undefined;
      if (followUp) {
        followUpTaskId = await createTicket(buildFollowUpTask(cleaned, followUp));
      }

      const activityId = await addActivity(
        stripBlank({ ...cleaned, ...(followUpTaskId ? { followUpTaskId } : {}) }) as ActivityInput
      );

      return activityId;
    } finally {
      setIsSubmitting(false);
    }
  };

  return { logActivity, isSubmitting };
}
